import { combineLatest, tap } from 'rxjs';
import {
  APIComponentAttributes,
  APIConstraintEnforcementMode,
  APIReferenceType,
  ArdoqId,
  ViewIds,
} from '@ardoq/api-types';
import { components$ } from 'streams/components/components$';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { WorkspaceAndModelLookup } from '@ardoq/data-model';
import { scopeDataOperations } from '@ardoq/scope-data';
import { isTruthy } from '@ardoq/common-helpers';
import {
  DependencyMatrixViewModel,
  DependencyMatrixViewSettings,
} from '../types';
import {
  action$,
  combineReducers,
  dispatchAction,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import { context$ } from 'streams/context/context$';
import { modelUpdateNotification$ } from 'modelInterface/modelUpdateNotification$';
import { EMPTY_LIST_DATA } from '../consts';
import { bypassLimitAction } from './actions';
import { isPresentationMode } from 'env';
import { focusedChrome$ } from 'scope/focusedChrome$';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';
import { buildViewModel } from './buildViewModel';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { ExtractStreamShape } from 'tabview/types';
import { getFocusedStream } from 'tabview/streams';
import { focusedComponentChanged } from 'tabview/actions';
import { HasViewInstanceId } from '@ardoq/graph';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { constraintsEnforcementMode$ } from 'streams/constraintsEnforcementMode$';
import { intendedRefTypes$ } from 'appModelStateEdit/propertiesEditor/intendedRefTypes$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { ARDOQ_DEFAULT_LOCALE } from '@ardoq/locale';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { getDefaultSort } from '@ardoq/common-helpers';

const VIEW_ID = ViewIds.DEPMATRIX;

const getWorkspaceLookups = (
  workspaceAndModel: WorkspaceAndModelLookup,
  workspaceId: ArdoqId,
  workspaceIds: ArdoqId[]
): {
  workspaceNamesByWorkspaceId: Record<ArdoqId, string>;
  referenceTypes: APIReferenceType[];
} => {
  const workspaceNamesByWorkspaceId = Object.fromEntries(
    workspaceIds
      .map(id => {
        const workspace = scopeDataOperations.getWorkspace(
          workspaceAndModel,
          id
        );
        return workspace ? ([id, workspace.name] as const) : null;
      })
      .filter(isTruthy)
  );
  const contextWorkspaceAndModel = scopeDataOperations.getWorkspaceAndModel(
    workspaceAndModel,
    workspaceId
  );
  const referenceTypes = contextWorkspaceAndModel
    ? scopeDataOperations.toWorkspaceWithTypes(contextWorkspaceAndModel)
        .referenceTypes
    : [];
  return { workspaceNamesByWorkspaceId, referenceTypes };
};

/**
 * Sanitize the persisted `referenceToWsId`: presentation replays slide state
 * verbatim, so a stale id (data drift) can reach the view. Resolving once
 * here means buildViewModel always gets a workspace that has components.
 */
const resolveReferenceToWsId = (
  {
    viewSettings,
    workspaceId,
    workspaceIds,
  }: {
    viewSettings: DependencyMatrixViewSettings;
    workspaceId: ArdoqId;
    workspaceIds: ArdoqId[];
  },
  components: APIComponentAttributes[]
): ArdoqId => {
  const { referenceToWsId } = viewSettings;
  if (
    referenceToWsId &&
    workspaceIds.includes(referenceToWsId) &&
    components.some(component => component.rootWorkspace === referenceToWsId)
  ) {
    return referenceToWsId;
  }
  return workspaceId;
};

const defaultViewSettings = getDefaultViewSettings(VIEW_ID, false);

const emptyState: DependencyMatrixViewModel = {
  viewSettings: defaultViewSettings,
  workspaceId: '',
  workspaceIds: [],
  workspaceNamesByWorkspaceId: {},
  referenceTypes: [],
  componentId: '',
  focusedComponentId: null,
  targetData: EMPTY_LIST_DATA,
  sourceData: EMPTY_LIST_DATA,
  sourceComponentItemDataById: {},
  targetComponentItemDataById: {},
  references: {},
  referenceCssClassNamesById: {},
  selectedReferenceTypeNameByComponentId: {},
  legendReferenceTypes: [],
  noConnectedComponents: false,
  isViewEmpty: true,
  isReadOnlyVisualization: false,
  bypassLimit: false,
  errors: [],
  permissionContext: permissionsOperations.createEmptyPermissionContext(),
  constraintEnforcementMode: 'off',
  intendedRefTypes: null,
  locale: ARDOQ_DEFAULT_LOCALE,
  // {} is the English fallback — t() uses its default text when keys are absent
  i18nLocale: {},
  errorInfoBoxLabels: getErrorInfoBoxLabels({}),
  sort: getDefaultSort(),
  userLocale: ARDOQ_DEFAULT_LOCALE,
  childrenByParent: new Map(),
};

type ResetArgs = Omit<DependencyMatrixViewModel, 'isReadOnlyVisualization'>;

const reset = ({
  viewSettings,
  componentId,
  workspaceId,
  workspaceIds,
  workspaceNamesByWorkspaceId,
  referenceTypes,
  focusedComponentId,
  targetData,
  sourceData,
  sourceComponentItemDataById,
  targetComponentItemDataById,
  references,
  referenceCssClassNamesById,
  selectedReferenceTypeNameByComponentId,
  legendReferenceTypes,
  noConnectedComponents,
  isViewEmpty,
  bypassLimit,
  errors,
  permissionContext,
  constraintEnforcementMode,
  intendedRefTypes,
  locale,
  i18nLocale,
  errorInfoBoxLabels,
  sort,
  userLocale,
  childrenByParent,
}: ResetArgs): ResetArgs => {
  if (!workspaceId) {
    return {
      ...emptyState,
      locale,
      i18nLocale,
      errorInfoBoxLabels,
      sort,
      userLocale,
      childrenByParent,
    };
  }

  return {
    viewSettings,
    workspaceId,
    workspaceIds,
    workspaceNamesByWorkspaceId,
    referenceTypes,
    componentId,
    focusedComponentId,
    targetData,
    sourceData,
    sourceComponentItemDataById,
    targetComponentItemDataById,
    references,
    referenceCssClassNamesById,
    selectedReferenceTypeNameByComponentId,
    legendReferenceTypes,
    noConnectedComponents,
    isViewEmpty,
    bypassLimit,
    errors,
    permissionContext,
    constraintEnforcementMode,
    intendedRefTypes,
    locale,
    i18nLocale,
    errorInfoBoxLabels,
    sort,
    userLocale,
    childrenByParent,
  };
};

const handleFocusedComponentChanged = (
  state: DependencyMatrixViewModel,
  focusedComponentId: ArdoqId | null
): DependencyMatrixViewModel => ({
  ...state,
  focusedComponentId:
    focusedComponentId !== state.focusedComponentId ? focusedComponentId : null,
});

export const getViewModel$ = ({ viewInstanceId }: HasViewInstanceId) => {
  const reset$ = combineLatest({
    viewSettings: getWorkspaceModeViewSettings$(VIEW_ID, viewInstanceId),
    context: context$,
    focusedComponentId: getFocusedStream(VIEW_ID),
    modelUpdate: modelUpdateNotification$,
    intendedRefTypes: intendedRefTypes$,
    locale: locale$,
    i18nLocale: i18nLocale$,
    userLocale: locale$,
    components: components$,
    workspaceAndModel: workspaceAndModel$,
    focusedChrome: focusedChrome$,
  });

  const resetReducer = (
    state: DependencyMatrixViewModel,
    {
      viewSettings,
      context,
      intendedRefTypes,
      locale,
      i18nLocale,
      userLocale,
      components,
      workspaceAndModel,
      focusedChrome,
    }: ExtractStreamShape<typeof reset$>
  ) => {
    const resolvedReferenceToWsId = resolveReferenceToWsId(
      {
        viewSettings,
        workspaceId: context.workspaceId,
        workspaceIds: context.workspaceIds,
      },
      components.models
    );
    const { workspaceNamesByWorkspaceId, referenceTypes } = getWorkspaceLookups(
      workspaceAndModel,
      context.workspaceId,
      context.workspaceIds
    );
    return {
      ...reset({
        ...buildViewModel({
          viewSettings,
          workspaceId: context.workspaceId,
          workspaceIds: context.workspaceIds,
          workspaceNamesByWorkspaceId,
          referenceTypes,
          resolvedReferenceToWsId,
          componentId: context.componentId,
          focusedComponentId: state.focusedComponentId,
          bypassLimit: isPresentationMode(),
          permissionContext: state.permissionContext,
          constraintEnforcementMode: state.constraintEnforcementMode,
          intendedRefTypes,
          i18nLocale,
          sort: context.sort,
          userLocale,
          childrenByParent: components.childrenByParent,
        }),
        locale,
        i18nLocale,
        errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
        sort: context.sort,
        userLocale,
        childrenByParent: components.childrenByParent,
      }),
      isReadOnlyVisualization:
        focusedChromeOperations.isReadOnlyVisualization(focusedChrome),
    };
  };

  const permissionContextReducer = (
    state: DependencyMatrixViewModel,
    permissionContext: PermissionContext
  ) => ({
    ...state,
    permissionContext,
  });

  const constraintEnforcementModeReducer = (
    state: DependencyMatrixViewModel,
    constraintEnforcementMode: APIConstraintEnforcementMode
  ) => ({
    ...state,
    constraintEnforcementMode,
  });

  const bypassLimitReducer = (state: DependencyMatrixViewModel) => ({
    ...state,
    ...buildViewModel({
      ...state,
      resolvedReferenceToWsId:
        state.viewSettings.referenceToWsId ?? state.workspaceId,
      bypassLimit: true,
    }),
  });

  return action$.pipe(
    combineReducers<DependencyMatrixViewModel>(emptyState, [
      streamReducer(reset$, resetReducer),
      reducer(focusedComponentChanged, handleFocusedComponentChanged),
      reducer(bypassLimitAction, bypassLimitReducer),
      streamReducer(currentUserPermissionContext$, permissionContextReducer),
      streamReducer(
        constraintsEnforcementMode$,
        constraintEnforcementModeReducer
      ),
    ]),
    tap(({ isViewEmpty, noConnectedComponents }) => {
      // Suppress the outer ViewpointEmptyState overlay when the local
      // "no connected components" doq is already shown, otherwise the
      // generic viewpoint "no data" doq renders on top of it.
      const shouldShowOuterEmptyState = isViewEmpty && !noConnectedComponents;
      dispatchAction(
        updateViewEmptyStateData({
          viewEmptyStateData: { isViewEmpty: shouldShowOuterEmptyState },
          viewId: VIEW_ID,
        })
      );
    })
  );
};
